import { getAnalytics } from "firebase/analytics";
import { getInstallations } from "firebase/installations";
import { PhoneAuthProvider, signInWithCredential } from "firebase/auth";
import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword as firebaseSignInWithEmail,
  updateProfile,
  linkWithPopup,
  linkWithCredential,
  linkWithPhoneNumber,
  EmailAuthProvider,
  getAdditionalUserInfo,
  type User,
  type ConfirmationResult,
} from "firebase/auth";
import {
  getFirestore,
  enableIndexedDbPersistence,
  doc,
  getDoc,
  getDocFromServer,
  setDoc,
  updateDoc,
  arrayUnion,
  runTransaction,
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  where,
  limit,
  Timestamp,
  increment,
  onSnapshot,
  writeBatch,
  deleteField,
  deleteDoc,
} from "firebase/firestore";
import { getFunctions } from "firebase/functions";

const firebaseConfig = {
  apiKey: "AIzaSyB16brls3ICISPqsH9ONea8Zf0AeeE2uWQ",
  authDomain: "auth.dailyhisaab.com",
  projectId: "corded-keel-471018-m0",
  storageBucket: "corded-keel-471018-m0.firebasestorage.app",
  messagingSenderId: "938822375178",
  appId: "1:938822375178:web:efc3b601349b159f76ccf3"
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);
export const functionsAsia = getFunctions(app, "asia-south1");

// ── 1. ANALYTICS & INSTALLATIONS ──
export const analytics = getAnalytics(app);
export const installations = getInstallations(app);
// ─────────────────────────────────

// ENABLE OFFLINE PERSISTENCE
enableIndexedDbPersistence(db).catch((err) => {
  if (err.code == 'failed-precondition') {
    console.warn('Offline persistence failed: Multiple tabs open.');
  } else if (err.code == 'unimplemented') {
    console.warn('Offline persistence is not supported by this browser/device.');
  }
});

let messagingInstance: any = null;

async function getMessagingInstance() {
  if (messagingInstance) return messagingInstance;
  try {
    const { getMessaging, isSupported } = await import("firebase/messaging");
    const supported = await isSupported();
    if (supported) {
      messagingInstance = getMessaging(app);
    }
  } catch (e) {
    console.warn("Firebase Messaging not supported:", e);
  }
  return messagingInstance;
}

export async function getTokenForNotifications(): Promise<string | null> {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;

    const messaging = await getMessagingInstance();
    if (!messaging) return null;

    const { getToken } = await import("firebase/messaging");

    const swRegistration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js"
    );

    const token = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });

    return token || null;
  } catch (err) {
    console.error("Failed to get notification token:", err);
    return null;
  }
}

export async function onForegroundMessage(callback: (payload: any) => void): Promise<(() => void) | null> {
  try {
    const messaging = await getMessagingInstance();
    if (!messaging) return null;
    const { onMessage } = await import("firebase/messaging");
    const unsubscribe = onMessage(messaging, callback);
    return unsubscribe;
  } catch (e) {
    console.warn("Failed to setup foreground messaging:", e);
    return null;
  }
}

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: "select_account",
});

const googleDriveProvider = new GoogleAuthProvider();
googleDriveProvider.addScope("https://www.googleapis.com/auth/spreadsheets");
googleDriveProvider.addScope("https://www.googleapis.com/auth/drive.file");
googleDriveProvider.setCustomParameters({
  prompt: "consent",
});

export async function loginWithGoogle() {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const accessToken = credential?.accessToken;

    if (accessToken) {
      localStorage.setItem("googleAccessToken", accessToken);
      localStorage.setItem("googleAccessTokenTime", Date.now().toString());
    }

    const user = result.user;
    const isNewUser = getAdditionalUserInfo(result)?.isNewUser === true;
    await createOrUpdateUserProfile(user, isNewUser);

    return { user, accessToken };
  } catch (error: any) {
    console.error("Login error:", error);
    throw error;
  }
}

export async function requestDriveAccess(): Promise<string | null> {
  try {
    const result = await signInWithPopup(auth, googleDriveProvider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const accessToken = credential?.accessToken;

    if (accessToken) {
      localStorage.setItem("googleAccessToken", accessToken);
      localStorage.setItem("googleAccessTokenTime", Date.now().toString());
      return accessToken;
    }
    return null;
  } catch (error: any) {
    console.error("Drive access request error:", error);
    throw error;
  }
}

export async function refreshGoogleToken(): Promise<string | null> {
  try {
    const result = await signInWithPopup(auth, googleDriveProvider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const accessToken = credential?.accessToken;

    if (accessToken) {
      localStorage.setItem("googleAccessToken", accessToken);
      localStorage.setItem("googleAccessTokenTime", Date.now().toString());
      return accessToken;
    }
    return null;
  } catch (error: any) {
    console.error("Token refresh error:", error);
    throw error;
  }
}

export function isAccessTokenValid(): boolean {
  const token = localStorage.getItem("googleAccessToken");
  const tokenTime = localStorage.getItem("googleAccessTokenTime");

  if (!token || !tokenTime) return false;

  const elapsed = Date.now() - parseInt(tokenTime);
  const fiftyMinutes = 50 * 60 * 1000;

  return elapsed < fiftyMinutes;
}

export async function logoutUser() {
  try {
    await signOut(auth);
    localStorage.removeItem("googleAccessToken");
    localStorage.removeItem("googleAccessTokenTime");
  } catch (error) {
    console.error("Logout error:", error);
    throw error;
  }
}

export function subscribeToAuthChanges(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback);
}

async function createOrUpdateUserProfile(user: User, isNewUserHint?: boolean) {
  const userRef = doc(db, "users", user.uid);

  // Server-authoritative existence check. A stale local cache could return
  // exists=false when the server actually has the doc — the original
  // getDoc(...) fell back to cache and wiped real users. See
  // DailyHisaab-Admin's incident report + workflow ww6owllmp for the full
  // analysis. This wipe is especially destructive in MobileWeb because the
  // seed also resets settings.plan to { tier:"basic", ... }, wiping any
  // Ultimate/Pro subscription instantly.
  let userSnap;
  try {
    userSnap = await getDocFromServer(userRef);
  } catch (err) {
    console.warn(
      `[firebase] createOrUpdateUserProfile: getDocFromServer failed for uid=${user.uid}; skipping profile init to avoid stale-cache wipe`,
      err,
    );
    return;
  }

  const rawDisplayName = user.displayName || "";
  const displayName = user.displayName || user.phoneNumber || user.email || "";
  const email = user.email || "";
  const phone = user.phoneNumber || "";

  if (!userSnap.exists() && isNewUserHint === true) {
    // Genuine fresh signup — server confirms no doc AND Firebase Auth confirms
    // this is a new user. Seed the full default profile with merge:true.
    await setDoc(
      userRef,
      {
        uid: user.uid,
        email: email,
        displayName: displayName,
        phoneNumber: phone,
        photoURL: null,
        createdAt: new Date().toISOString(),
        settings: {
          spreadsheetId: null,
          isSyncEnabled: false,
          profileCompleted: false,
          invoiceFooter: "",
          invoiceTerms: "",
          invoiceLayout: "standard",
          printQrCode: false,
          rewardPoints: 0,
          gstInvoiceCounter: 0,
          // Canonical subscription shape
          plan: {
            tier: "basic",
            expiresAt: null,
            trial: null,
            billing: null,
            oneTime: null,
            history: [],
          },
          trialsUsed: { any: false, consumedFor: null, consumedAt: null },
          devices: { limit: 1, active: [] },
        },
        businessProfile: {
          ownerName: displayName,
          businessName: "",
          businessAddress: "",
          businessEmail: email,
          businessPhone: phone,
          businessWebsite: "",
          businessLogo: null,
        },
      },
      { merge: true },
    );
    try {
      await getOrCreateReferralCode(user.uid);
    } catch (e) { }
    const pendingCode = sessionStorage.getItem("pendingReferralCode");
    if (pendingCode) {
      sessionStorage.removeItem("pendingReferralCode");
      try {
        await applyReferralCode(pendingCode, user.uid, displayName || phone || "Friend");
      } catch (e) { }
    }
    return;
  }

  if (!userSnap.exists() && isNewUserHint !== true) {
    // Server says no doc, but Auth did not flag this as a new account. Either
    // the caller is a linking flow (never mints new) or the hint wasn't passed.
    // Refuse to seed — a merge write of lastLoginAt preserves everything else
    // if the real doc materializes on the next read.
    console.warn(
      `[firebase] createOrUpdateUserProfile: server reports missing doc for uid=${user.uid} but Auth did not flag as new user — NOT seeding defaults`,
    );
    try {
      await setDoc(userRef, { lastLoginAt: new Date().toISOString() }, { merge: true });
    } catch (e) { }
    return;
  }

  // Existing doc — merge only the fields we know we want to update.
  // Do NOT overwrite the stored displayName with a phone-number fallback for
  // phone-auth users (user.displayName is null on the Auth object).
  const updates: Record<string, any> = {
    lastLoginAt: new Date().toISOString(),
  };
  if (email) updates.email = email;
  if (rawDisplayName) updates.displayName = rawDisplayName;
  if (phone) updates.phoneNumber = phone;
  await setDoc(userRef, updates, { merge: true });
}

export function setupRecaptcha(elementId: string): RecaptchaVerifier {
  const verifier = new RecaptchaVerifier(auth, elementId, {
    size: "invisible",
  });
  return verifier;
}

// --- SEND PHONE OTP ---
export async function sendPhoneOTP(
  phoneNumber: string,
  recaptchaVerifier: RecaptchaVerifier,
): Promise<ConfirmationResult> {
  return await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
}

// --- VERIFY PHONE OTP ---
export async function verifyPhoneOTP(
  confirmationResult: ConfirmationResult,
  otp: string,
) {
  const result = await confirmationResult.confirm(otp);
  if (result.user) {
    const isNewUser = getAdditionalUserInfo(result)?.isNewUser === true;
    await createOrUpdateUserProfile(result.user, isNewUser);
    return result.user;
  }
  throw new Error("Verification failed.");
}

export async function signUpWithEmail(
  email: string,
  password: string,
  name: string,
) {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  if (name) {
    await updateProfile(result.user, { displayName: name });
  }
  // createUserWithEmailAndPassword ALWAYS creates a new Auth user.
  await createOrUpdateUserProfile(result.user, true);
  return result.user;
}

export async function signInWithEmail(email: string, password: string) {
  const result = await firebaseSignInWithEmail(auth, email, password);
  // signInWithEmailAndPassword ONLY works for existing accounts.
  await createOrUpdateUserProfile(result.user, false);
  return result.user;
}

export function getLinkedProviders(): { providerId: string; email?: string | null; phoneNumber?: string | null }[] {
  const currentUser = auth.currentUser;
  if (!currentUser) return [];
  return currentUser.providerData.map((p) => ({
    providerId: p.providerId,
    email: p.email,
    phoneNumber: p.phoneNumber,
  }));
}

export async function linkGoogleAccount() {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("No user logged in");
  const result = await signInWithPopup(auth, googleProvider);
  const credential = GoogleAuthProvider.credentialFromResult(result);
  if (credential?.accessToken) {
    localStorage.setItem("googleAccessToken", credential.accessToken);
    localStorage.setItem("googleAccessTokenTime", Date.now().toString());
  }
  // Linking a provider to an already signed-in account never mints a new user.
  await createOrUpdateUserProfile(result.user, false);
  return result.user;
}

// --- LINK PHONE ---
export async function linkPhoneToAccount(
  phoneNumber: string,
  recaptchaVerifier: RecaptchaVerifier,
): Promise<ConfirmationResult> {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("No user logged in");
  return await linkWithPhoneNumber(currentUser, phoneNumber, recaptchaVerifier);
}

export async function confirmPhoneLinkOTP(
  confirmationResult: ConfirmationResult,
  otp: string,
) {
  const result = await confirmationResult.confirm(otp);
  if (result.user) {
    // Phone-linking never mints a new user.
    await createOrUpdateUserProfile(result.user, false);
    return result.user;
  }
  throw new Error("Phone linking failed.");
}

export async function linkEmailToAccount(email: string, password: string) {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("No user logged in");
  const credential = EmailAuthProvider.credential(email, password);
  const result = await linkWithCredential(currentUser, credential);
  // Email-linking never mints a new user.
  await createOrUpdateUserProfile(result.user, false);
  return result.user;
}

// DEPRECATED: tier is derived from `settings.plan.tier`.
// Kept as no-op for legacy callsites during transition.
export async function updateUserMode(_userId: string, _mode: "pro" | "normal" | "ultimate") {
  console.warn("[firebase] updateUserMode is deprecated. Use plan.tier via canonical flow.");
}

export type { ConfirmationResult };

export type UserMode = "normal" | "pro" | "ultimate";

export interface BusinessProfile {
  ownerName: string;
  businessName: string;
  businessAddress: string;
  businessEmail: string;
  businessPhone: string;
  businessWebsite: string;
  businessLogo?: string | null;
  isGstEnabled?: boolean;
  gstInclusive?: boolean;
  gstin?: string;
  cin?: string;
  fssai?: string;
  phoneVerified?: boolean;
  shopType?: import("./onboardingSeeds").ShopType;
}

export interface UserSettings {
  spreadsheetId: string | null;
  isSyncEnabled: boolean;
  profileCompleted: boolean;
  businessName: string;
  rewardPoints: number;
  businessLogo?: string | null;
  isGstEnabled?: boolean;
  gstInclusive?: boolean;
  gstin?: string;
  cin?: string;
  fssai?: string;
  signupDate?: string;
  signupBonusAwarded?: boolean;
  readNotificationIds?: string[];
  dismissedNotificationIds?: string[];
  hideFavoriteProducts?: boolean;
  hideSalesSummary?: boolean;
  printerPaperWidth?: "58" | "80" | "A4";
  printerPrintLogo?: boolean;
  deletionRequestedAt?: string | null;
  deletionReason?: string;
  invoiceFooter?: string;
  invoiceTerms?: string;
  invoiceLayout?: 'standard' | 'basic';
  printQrCode?: boolean;
  labelPrinterLanguage?: "escpos" | "tspl" | "zpl";
  labelWidth?: number;
  labelHeight?: number;
  gstInvoiceCounter?: number;
  uiMode?: "pro" | "ultimate" | "normal"; // Pro/Ultimate users can flip to "normal" UI view

  // Canonical subscription shape (single source of truth)
  plan?: import("./planTypes").Plan;
  trialsUsed?: import("./planTypes").TrialsUsed;
  devices?: import("./planTypes").DevicesState;

  // Ultimate staff-sharing
  staffLocks?: import("./planTypes").StaffLocks;
  desktopInvites?: import("./planTypes").DesktopInvite[];

  // AI SETTINGS (public flags only — raw keys in private subcollection)
  aiSettings?: {
    customKeyProvider?: "gemini" | "openai" | null;
    /** Deprecated, kept for back-compat read of pre-unification docs. Do not write. */
    hasCustomGeminiKey?: boolean;
    /** Deprecated, kept for back-compat read of pre-unification docs. Do not write. */
    hasCustomOpenaiKey?: boolean;
    adminImagesUsedToday?: number;
    adminTaxonomyUsedToday?: number;
    lastImageDate?: string;
    copilotUsedToday?: number;
    copilotLastDate?: string;
    copilotLifetimeUsed?: number;
  };
}

export interface UserData {
  settings: UserSettings;
  businessProfile: BusinessProfile;
}

export async function getUserSettings(userId: string): Promise<UserSettings> {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    const data = userSnap.data();
    const s = data.settings || {};

    return {
      spreadsheetId: s.spreadsheetId || null,
      isSyncEnabled: s.isSyncEnabled || false,
      rewardPoints: s.rewardPoints ?? 0,
      profileCompleted: s.profileCompleted || !!data.businessProfile?.businessName || false,
      businessName: data.businessProfile?.businessName || s.businessName || "",
      businessLogo: data.businessProfile?.businessLogo || null,
      isGstEnabled: s.isGstEnabled || data.businessProfile?.isGstEnabled || false,
      gstInclusive: s.gstInclusive || data.businessProfile?.gstInclusive || false,
      gstin: s.gstin || data.businessProfile?.gstin || "",
      cin: s.cin || data.businessProfile?.cin || "",
      fssai: s.fssai || data.businessProfile?.fssai || "",
      signupBonusAwarded: s.signupBonusAwarded || false,
      readNotificationIds: s.readNotificationIds || [],
      dismissedNotificationIds: s.dismissedNotificationIds || [],
      hideFavoriteProducts: s.hideFavoriteProducts || false,
      hideSalesSummary: s.hideSalesSummary || false,
      printerPaperWidth: s.printerPaperWidth || "58",
      printerPrintLogo: s.printerPrintLogo !== false,
      deletionRequestedAt: s.deletionRequestedAt || null,
      deletionReason: s.deletionReason || "",
      invoiceFooter: s.invoiceFooter || "",
      invoiceTerms: s.invoiceTerms || "",
      invoiceLayout: s.invoiceLayout || "standard",
      printQrCode: s.printQrCode || false,
      labelPrinterLanguage: (s.labelPrinterLanguage as "escpos" | "tspl" | "zpl") || "escpos",
      labelWidth: s.labelWidth,
      labelHeight: s.labelHeight,
      gstInvoiceCounter: s.gstInvoiceCounter ?? data.gstInvoiceCounter ?? 0,
      uiMode: s.uiMode,

      // Canonical subscription
      plan: s.plan || undefined,
      trialsUsed: s.trialsUsed || undefined,
      devices: s.devices || undefined,

      aiSettings: s.aiSettings || undefined,
    };
  }

  return {
    spreadsheetId: null,
    isSyncEnabled: false,
    profileCompleted: false,
    businessName: "",
    rewardPoints: 0,
    businessLogo: null,
    signupBonusAwarded: false,
    invoiceFooter: "",
    invoiceTerms: "",
    invoiceLayout: "standard",
    printQrCode: false,
    labelPrinterLanguage: "escpos",
  };
}

export function subscribeToUserSettings(
  userId: string,
  callback: (settings: any) => void,
): () => void {
  const userRef = doc(db, "users", userId);
  return onSnapshot(userRef, (snap) => {
    if (snap.exists()) {
      const data = snap.data();
      const s = data.settings || {};
      callback({
        rewardPoints: s.rewardPoints ?? 0,
        readNotificationIds: (s.readNotificationIds as string[]) || [],
        dismissedNotificationIds: (s.dismissedNotificationIds as string[]) || [],
        hideFavoriteProducts: (s.hideFavoriteProducts as boolean) || false,
        hideSalesSummary: (s.hideSalesSummary as boolean) || false,
        printerPaperWidth: (s.printerPaperWidth as "58" | "80" | "A4") || "58",
        printerPrintLogo: s.printerPrintLogo !== false,
        deletionRequestedAt: s.deletionRequestedAt || null,
        deletionReason: s.deletionReason || "",
        invoiceFooter: (s.invoiceFooter as string) || "",
        invoiceTerms: (s.invoiceTerms as string) || "",
        invoiceLayout: (s.invoiceLayout as 'standard' | 'basic') || "standard",
        printQrCode: (s.printQrCode as boolean) || false,
        labelPrinterLanguage: (s.labelPrinterLanguage as "escpos" | "tspl" | "zpl") || "escpos",
        gstInvoiceCounter: s.gstInvoiceCounter ?? data.gstInvoiceCounter ?? 0,
        uiMode: s.uiMode || null,

        // Canonical subscription shape
        plan: s.plan || null,
        trialsUsed: s.trialsUsed || null,
        devices: s.devices || null,

        aiSettings: s.aiSettings || null,
      });
    }
  });
}

export async function getBusinessProfile(
  userId: string,
): Promise<BusinessProfile> {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    const data = userSnap.data();
    return {
      ownerName: data.businessProfile?.ownerName || data.displayName || "",
      businessName: data.businessProfile?.businessName || "",
      businessAddress: data.businessProfile?.businessAddress || "",
      businessEmail: data.businessProfile?.businessEmail || data.email || "",
      businessPhone: data.businessProfile?.businessPhone || "",
      businessWebsite: data.businessProfile?.businessWebsite || "",
      businessLogo:
        data.businessProfile?.businessLogo ||
        data.settings?.businessLogo ||
        null,
      isGstEnabled:
        data.businessProfile?.isGstEnabled ||
        data.settings?.isGstEnabled ||
        false,
      gstInclusive:
        data.businessProfile?.gstInclusive ||
        data.settings?.gstInclusive ||
        false,
      gstin: data.businessProfile?.gstin || data.settings?.gstin || "",
      cin: data.businessProfile?.cin || data.settings?.cin || "",
      fssai: data.businessProfile?.fssai || data.settings?.fssai || "",
    };
  }

  return {
    ownerName: "",
    businessName: "",
    businessAddress: "",
    businessEmail: "",
    businessPhone: "",
    businessWebsite: "",
    businessLogo: null,
  };
}

/**
 * Read the admin-written business verification doc:
 *   users/{uid}/verifications/business { verified:boolean, verifiedName:string }
 * A missing doc OR any read error MUST be treated as NOT verified (returns
 * null, never throws) — WhatsApp-OTP signups have no users/{uid} doc until
 * their first profile save.
 */
export async function getBusinessVerification(
  uid: string,
): Promise<{ verified: boolean; verifiedName: string } | null> {
  try {
    const ref = doc(db, "users", uid, "verifications", "business");
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      verified: data.verified === true,
      verifiedName: String(data.verifiedName ?? ""),
    };
  } catch {
    return null;
  }
}

export async function updateUserSettings(
  userId: string,
  settings: Partial<UserSettings>,
) {
  const userRef = doc(db, "users", userId);
  const dataToUpdate: any = {};

  Object.entries(settings).forEach(([key, value]) => {
    if (value !== undefined) {
      dataToUpdate[`settings.${key}`] = value;
    }
  });

  if (Object.keys(dataToUpdate).length > 0) {
    await updateDoc(userRef, dataToUpdate);
  }
}

export async function saveBusinessProfile(
  userId: string,
  profile: BusinessProfile,
) {
  const userRef = doc(db, "users", userId);
  await setDoc(
    userRef,
    {
      businessProfile: profile,
      settings: {
        profileCompleted: true,
        signupDate: new Date().toISOString() // <-- Added exactly here!
      }
    },
    { merge: true },
  );
}

export async function clearBusinessProfileLogo(userId: string): Promise<void> {
  const userRef = doc(db, "users", userId);
  await updateDoc(userRef, { "businessProfile.businessLogo": deleteField() });
}

export async function publishInvoiceToFirestore(
  userId: string,
  orderId: string,
  invoiceData: any,
) {
  const invoiceRef = doc(db, "users", userId, "public_invoices", orderId);
  setDoc(invoiceRef, invoiceData).catch(() => { });
}

export async function getPublicInvoice(orderId: string, userId?: string) {
  try {
    const fetchPromise = (async () => {
      if (userId) {
        const newRef = doc(db, "users", userId, "public_invoices", orderId);
        const newSnap = await getDoc(newRef);
        if (newSnap.exists()) return newSnap.data();
      }
      const oldRef = doc(db, "public_invoices", orderId);
      const oldSnap = await getDoc(oldRef);
      return oldSnap.exists() ? oldSnap.data() : null;
    })();

    return await Promise.race([
      fetchPromise,
      new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), 2000))
    ]);
  } catch (err) {
    return null;
  }
}

export async function deletePublicInvoice(userId: string, orderId: string) {
  try {
    const invoiceRef = doc(db, "users", userId, "public_invoices", orderId);
    await deleteDoc(invoiceRef);

    const pointerQuery = query(
      collection(db, "public_invoices"),
      where("orderId", "==", orderId),
      where("userId", "==", userId)
    );
    const pointerSnap = await getDocs(pointerQuery);

    const deletePromises = pointerSnap.docs.map(pointerDoc => deleteDoc(pointerDoc.ref));
    await Promise.all(deletePromises);

  } catch (err) {
    console.error("Failed to delete public invoice:", err);
  }
}

/**
 * Add/update a custom category in canonical subcollection.
 * Matches Desktop shape: users/{uid}/categories/{catId} → { name, color?, hasExplicitColor, createdAt }
 */
export async function addCustomCategory(
  userId: string,
  name: string,
  color?: string,
): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) return;
  const catId = trimmed.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  if (!catId) return;
  const catRef = doc(db, "users", userId, "categories", catId);
  await setDoc(
    catRef,
    {
      name: trimmed,
      ...(color ? { color, hasExplicitColor: true } : {}),
      createdAt: new Date().toISOString(),
    },
    { merge: true },
  );
}

export async function getNextGstInvoiceNumber(userId: string): Promise<string> {
  const userRef = doc(db, "users", userId);

  try {
    const fetchPromise = runTransaction(db, async (transaction) => {
      const userSnap = await transaction.get(userRef);
      let currentCounter = 0;
      if (userSnap.exists()) {
        const data = userSnap.data();
        // Read canonical first, fall back to legacy root path during migration
        currentCounter = data.settings?.gstInvoiceCounter ?? data.gstInvoiceCounter ?? 0;
      }
      const nextCounter = currentCounter + 1;
      // Write canonical only
      transaction.set(userRef, { settings: { gstInvoiceCounter: nextCounter } }, { merge: true });
      return nextCounter;
    });

    const newCounter = await Promise.race([
      fetchPromise,
      new Promise<number>((_, reject) => setTimeout(() => reject(new Error("Timeout")), 2000))
    ]);

    return newCounter.toString().padStart(4, "0");
  } catch (err) {
    return `OFF-${Math.floor(1000 + Math.random() * 9000)}`;
  }
}

export interface RewardEvent {
  id?: string;
  type: "earn" | "burn";
  points: number;
  date: string;
  description: string;
  action: string;
  orderId?: string;
}

export async function saveRewardEvent(
  userId: string,
  event: Omit<RewardEvent, "id">,
) {
  const eventId = `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const eventData: RewardEvent = {
    ...event,
    id: eventId,
    date: event.date || new Date().toISOString(),
  };
  const eventRef = doc(db, "users", userId, "rewards", eventId);
  setDoc(eventRef, eventData).catch(() => { });
}

export async function loadRewardHistory(
  userId: string,
): Promise<RewardEvent[]> {
  try {
    const rewardsRef = collection(db, "users", userId, "rewards");
    const q = query(rewardsRef, orderBy("date", "desc"));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      return snapshot.docs.map((d) => d.data() as RewardEvent);
    }
  } catch (e) { }
  return [];
}

export async function atomicUpdateRewardPoints(
  userId: string,
  points: number,
): Promise<number> {
  const userRef = doc(db, "users", userId);

  try {
    const fetchPromise = runTransaction(db, async (tx) => {
      const userSnap = await tx.get(userRef);
      let currentPoints = 0;
      if (userSnap.exists()) {
        const data = userSnap.data();
        currentPoints = data.settings?.rewardPoints ?? 0;
      }
      const newPoints = Math.max(0, currentPoints + points);
      tx.update(userRef, { "settings.rewardPoints": newPoints });
      return newPoints;
    });

    return await Promise.race([
      fetchPromise,
      new Promise<number>((_, reject) => setTimeout(() => reject(new Error("Timeout")), 2000))
    ]);
  } catch (err) {
    updateDoc(userRef, { "settings.rewardPoints": increment(points) }).catch(() => { });
    return -1;
  }
}

export { type User };

export interface ReferralDoc {
  id: string;
  referrerUid: string;
  refereeUid: string;
  refereeName: string;
  referralCode: string;
  status: "pending" | "completed";
  steps: {
    signedUp: boolean;
    addedProduct: boolean;
    madeSale: boolean;
  };
  createdAt: string;
  completedAt: string | null;
  pointsAwarded: boolean;
}

export async function getOrCreateReferralCode(uid: string): Promise<string> {
  const userRef = doc(db, "users", uid);

  // Server-authoritative reads. A cache false-negative here would mint a NEW
  // referral code and clobber the user's existing shared one — same class of
  // bug as the profile wipe.
  let userSnap;
  try {
    userSnap = await getDocFromServer(userRef);
  } catch {
    // Offline / network — return a deterministic best-effort code without
    // writing. Any real code will be discovered on the next online call.
    const cleanUid = uid.replace(/[^A-Za-z0-9]/g, '');
    return cleanUid.slice(0, 8).toUpperCase();
  }

  if (userSnap.exists() && userSnap.data().referralCode) {
    return userSnap.data().referralCode as string;
  }
  const cleanUid = uid.replace(/[^A-Za-z0-9]/g, '');
  const code = cleanUid.slice(0, 8).toUpperCase();

  // Collision check also server-first.
  let codeSnap;
  try {
    codeSnap = await getDocFromServer(doc(db, "referralCodes", code));
  } catch {
    codeSnap = null;
  }
  const finalCode = codeSnap && codeSnap.exists() && codeSnap.data().uid !== uid
    ? (cleanUid.slice(0, 6) + cleanUid.slice(-2)).toUpperCase()
    : code;

  Promise.all([
    setDoc(userRef, { referralCode: finalCode }, { merge: true }),
    setDoc(doc(db, "referralCodes", finalCode), { uid }, { merge: true }),
  ]).catch(() => { });

  return finalCode;
}

export async function applyReferralCode(
  code: string,
  refereeUid: string,
  refereeName: string,
): Promise<void> {
  try {
    const cleanCode = code.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    const codeRef = doc(db, "referralCodes", cleanCode);
    const codeSnap = await getDoc(codeRef);
    if (!codeSnap.exists()) return;
    const referrerUid = codeSnap.data().uid as string;
    if (referrerUid === refereeUid) return;

    const referralRef = doc(collection(db, "referrals"));
    Promise.all([
      setDoc(referralRef, {
        referrerUid,
        refereeUid,
        refereeName: refereeName || "Friend",
        referralCode: cleanCode,
        status: "pending",
        steps: { signedUp: true, addedProduct: false, madeSale: false },
        createdAt: new Date().toISOString(),
        completedAt: null,
        pointsAwarded: false,
      }),
      setDoc(doc(db, "users", refereeUid), { referredBy: code.toUpperCase() }, { merge: true })
    ]).catch(() => { });
  } catch (err) { }
}

export async function updateReferralStep(
  refereeUid: string,
  step: "addedProduct" | "madeSale",
): Promise<void> {
  try {
    const q = query(
      collection(db, "referrals"),
      where("refereeUid", "==", refereeUid),
      where("status", "==", "pending"),
      limit(1),
    );

    const snap = await getDocs(q);
    if (snap.empty) return;
    const refDoc = snap.docs[0];
    const data = refDoc.data() as ReferralDoc;
    if (data.steps[step]) return;
    const updatedSteps = { ...data.steps, [step]: true };
    const isComplete = updatedSteps.signedUp && updatedSteps.addedProduct && updatedSteps.madeSale;

    updateDoc(
      refDoc.ref,
      {
        [`steps.${step}`]: true,
        ...(isComplete ? { status: "completed", completedAt: new Date().toISOString() } : {}),
      }
    ).catch(() => { });
  } catch (err) { }
}

export async function getPendingReferralRewards(
  referrerUid: string,
): Promise<{ id: string; refereeName: string }[]> {
  try {
    const q = query(
      collection(db, "referrals"),
      where("referrerUid", "==", referrerUid),
      where("status", "==", "completed"),
      where("pointsAwarded", "==", false),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, refereeName: (d.data() as ReferralDoc).refereeName }));
  } catch (err) {
    return [];
  }
}

export async function markReferralRewarded(referralId: string): Promise<void> {
  updateDoc(doc(db, "referrals", referralId), { pointsAwarded: true }).catch(() => { });
}

export async function getUserReferrals(referrerUid: string): Promise<ReferralDoc[]> {
  try {
    const q = query(
      collection(db, "referrals"),
      where("referrerUid", "==", referrerUid),
      orderBy("createdAt", "desc"),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as ReferralDoc));
  } catch (err) {
    return [];
  }
}
